import React from 'react';
import { X, Check, Shield, Brain, Zap, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';

const AccountabilitySection = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900 text-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-6"
          >
            <div className="inline-flex items-center gap-2 bg-yellow-500/20 text-yellow-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Shield className="w-4 h-4" />
              Revolutionary Accountability Model
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Finally, software that's{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                responsible for your success
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Other tools make you do the work. We do it all for you.
            </p>
          </motion.div>
        </div>

        {/* Comparison Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Traditional Tools - Left Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-red-900/20 border border-red-500/30 rounded-2xl p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <X className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-2xl font-bold text-red-400">Traditional Tools</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <X className="w-5 h-5 text-red-400 mt-1 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">YOU write 50+ emails daily</div>
                  <div className="text-gray-400 text-sm">3 hours/day staring at blank subject lines</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <X className="w-5 h-5 text-red-400 mt-1 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">YOU scrape LinkedIn for 6 hours</div>
                  <div className="text-gray-400 text-sm">40% bounce rate kills your domain reputation</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <X className="w-5 h-5 text-red-400 mt-1 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">YOU manage deliverability</div>
                  <div className="text-gray-400 text-sm">Set up mailboxes, warm them, monitor spam rates</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <X className="w-5 h-5 text-red-400 mt-1 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">YOU analyze and optimize</div>
                  <div className="text-gray-400 text-sm">Figure out what's working and adjust manually</div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-red-900/30 rounded-lg border border-red-500/20">
              <div className="text-center">
                <div className="text-sm text-red-400 mb-1">When it fails...</div>
                <div className="font-bold text-lg">It's YOUR fault</div>
              </div>
            </div>
          </motion.div>

          {/* Munger - Right Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-green-900/20 border border-green-500/30 rounded-2xl p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-green-400">Munger (Agency Model)</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">WE write 1,000 unique emails in 60 seconds</div>
                  <div className="text-gray-400 text-sm">Each mentions their recent LinkedIn post</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">WE find the contacts</div>
                  <div className="text-gray-400 text-sm">AI identifies and verifies your ideal prospects</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">WE handle deliverability</div>
                  <div className="text-gray-400 text-sm">Automatic mailbox setup, warming, and monitoring</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">WE optimize continuously</div>
                  <div className="text-gray-400 text-sm">AI learns and improves 24/7 without your input</div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-green-900/30 rounded-lg border border-green-500/20">
              <div className="text-center">
                <div className="text-sm text-green-400 mb-1">If you don't get meetings...</div>
                <div className="font-bold text-lg">It's OUR responsibility</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-yellow-500/20 to-green-500/20 rounded-2xl p-8 border border-yellow-500/30">
            <div className="flex items-center justify-center gap-4 mb-4">
              <Brain className="w-8 h-8 text-yellow-400" />
              <Zap className="w-8 h-8 text-green-400" />
              <TrendingUp className="w-8 h-8 text-blue-400" />
            </div>
            
            <h3 className="text-2xl font-bold mb-3">
              It's Like Hiring a $15k/mo Email Agency — Without The Agency
            </h3>
            
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              You provide one input – your product brief. We handle everything else. 
              No learning curve. No manual work. Just meetings in your calendar.
            </p>
            
            {/* Satisfaction note removed as per updated policy */}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AccountabilitySection; 